import {
  SOURCE_ADMIN_SMS,
  SOURCE_TCAPI,
} from '@rsos/constants/locationSources';
import {
  gspx,
  GPX_EVENT_TYPES,
  GPX_FEATURES,
  GPX_CAPABILITIES,
  GPX_ROUTES,
} from '@rsos/gainsight';
import { hashTrackingId, hashTrackingToken } from './hashTrackingId';

const LOCATION_REQUEST_EVENT_NAMES: Record<string, string> = {
  CLICKED: 'location_request_clicked',
  GENERATED: 'location_request_generated',
  SENT: 'location_request_sent',
  FAILED: 'location_request_failed',
};

const defaultTrackEvent = {
  feature: GPX_FEATURES.LOCATION_REQUEST,
  capability: GPX_CAPABILITIES.LOCATION_REQUEST,
  route: GPX_ROUTES.COMMUNICATOR,
};

/**
 * The text pathway a location request was sent over. Mirrors the pathway names
 * product uses in Amplitude rather than the internal conversation `source`
 * values, so `pathway_type` reads the same way in a dashboard as in a ticket.
 */
export type LocationRequestPathway =
  | 'TF911'
  | 'TCAPI'
  | 'TTAdmin'
  | 'RCS'
  | 'unknown';

/**
 * Why a click never produced a location request link. Every click resolves to
 * exactly one of generated or failed, so these values are what attribute a
 * drop-off — the counts alone cannot, because most of these have nothing to do
 * with the minting API.
 */
export type LocationRequestFailureReason =
  /** The chat had ended or had not mounted, so it could not accept a message. */
  | 'chat_unavailable'
  /** No location-request message is configured for the authority. */
  | 'message_unavailable'
  /** A smart phrase in the message failed to resolve — not necessarily the link. */
  | 'phrase_resolution_failed'
  /** The authority edited the link phrase out of the message. */
  | 'link_phrase_absent';

/**
 * Maps a conversation `source` (see @rsos/constants/locationSources) to its
 * pathway name. Returns 'unknown' rather than guessing — a visible bucket in
 * Amplitude is easier to spot and fix than a wrong pathway label.
 */
export const pathwayFromConversationSource = (
  source: string | null | undefined,
): LocationRequestPathway => {
  switch (source) {
    case SOURCE_TCAPI:
      return 'TCAPI';
    case SOURCE_ADMIN_SMS:
      return 'TTAdmin';
    default:
      return 'unknown';
  }
};

// Must stay in sync with CALLER_ID_BOUND_URL_PATTERNS in
// components/JurisdictionView/ActionBar/OutboundSMS/helpers.ts — a unit test
// asserts the two agree. The token is a uuid4 hex minted by
// POST /v1/public-services/location-share/request-url.
const LOCATION_REQUEST_URL_PATTERN =
  /assist\/locationrequest\?token=([A-Za-z0-9]+)/;

/**
 * Returns the location-request token if `text` contains a location request
 * link, otherwise null. Doubles as the "does this message carry a location
 * link" predicate, since the token is what ties the three events together into
 * one request's lifecycle.
 */
export const extractLocationRequestToken = (
  text: string | null | undefined,
): string | null => text?.match(LOCATION_REQUEST_URL_PATTERN)?.[1] ?? null;

/**
 * The fields this module reads off a TalkJS message. A real Talk.Message
 * satisfies this; tests pass a plain object instead of faking the full surface.
 */
export interface TrackableChatMessage {
  conversation: { id: string };
  /** True when the current user sent the message. */
  isByMe: boolean;
  /** Message text; empty for media and location messages. */
  body: string;
}

interface LocationRequestContext {
  pathwayType: LocationRequestPathway;
  /** Raw caller ID; hashed before it leaves this module. */
  callerID: string | null | undefined;
  conversationId?: string;
  /** True for the outbound SMS composer, false for an in-call chat. */
  isOutbound?: boolean;
}

type LocationRequestTokenContext = LocationRequestContext & { token: string };

/**
 * The subset of a resolved preconfigured message this module reads. Structural
 * so callers can pass `resolvedPhrases` straight through without a cast.
 */
interface ResolvedLocationPhrases {
  LocationRequestLink?: string;
}

// The raw token is a live 15-minute capability: POST location-share/location
// takes no auth beyond it, so holding one is enough to write a falsified
// location for the caller and have it pushed to the dispatcher's map. It stays
// in this tab — only hashTrackingToken output is ever reported, which is all
// Amplitude needs since token_id is purely a join key across the three events.
//
// A location-request token is minted per click and should be reported sent at
// most once, so tracking by token makes the sent event idempotent. That covers
// the outbound-SMS 409 hand-off (which sends through TF911Chat's TalkJS
// session, firing onMessage for a message we already counted) and the race
// where TF911Chat mounts in time to observe a message the backend just sent.
//
// The generated side keeps the whole context, not just the token, so the sent
// event can inherit `is_outbound` from wherever the link was minted rather than
// from whichever surface happened to observe the send.
//
// Bounded because the server-side token TTL is 15 minutes; the cap only guards
// against a pathological session. State is per-tab, so the same link open in
// two tabs will double-count — accepted.
const MAX_TRACKED_TOKENS = 200;
const generatedContexts = new Map<string, LocationRequestContext>();
const sentTokens = new Set<string>();

const rememberGenerated = (
  token: string,
  context: LocationRequestContext,
): void => {
  if (generatedContexts.size >= MAX_TRACKED_TOKENS) {
    generatedContexts.clear();
  }
  generatedContexts.set(token, context);
};

const rememberSent = (token: string): void => {
  if (sentTokens.size >= MAX_TRACKED_TOKENS) {
    sentTokens.clear();
  }
  sentTokens.add(token);
};

/** Test-only: clears the per-tab token state between cases. */
export const __resetLocationRequestTracking = (): void => {
  generatedContexts.clear();
  sentTokens.clear();
};

const entitiesFor = ({
  callerID,
  conversationId,
}: LocationRequestContext): Record<string, unknown> => ({
  caller_id_hash: hashTrackingId(callerID),
  conversation_id: conversationId,
});

const additionalKeysFor = ({
  pathwayType,
  isOutbound,
}: LocationRequestContext): Record<string, unknown> => ({
  pathway_type: pathwayType,
  is_outbound: !!isOutbound,
});

// agency_id is not passed: gspx fills ecc_account_ids.source from the redux
// data provider's current PSAP account_id, and adds entities.payload_id /
// entities.call_start_time for the selected call.

/**
 * Tracks the intent to request a location — the "Request Location" button was
 * pressed. Always followed by either a generated or a failed event, so a
 * drop-off is attributed by `failure_reason` rather than by subtracting counts.
 */
export const trackLocationRequestClicked = (
  context: LocationRequestContext,
): void => {
  gspx.trackEvent({
    ...defaultTrackEvent,
    name: LOCATION_REQUEST_EVENT_NAMES.CLICKED,
    type: GPX_EVENT_TYPES.CLICK,
    entities: entitiesFor(context),
    additionalKeys: additionalKeysFor(context),
  });
};

/**
 * Tracks that a location request link and token were successfully minted and
 * placed in the message field. Not yet sent — the dispatcher may still edit or
 * abandon the draft.
 */
export const trackLocationRequestGenerated = ({
  token,
  ...context
}: LocationRequestTokenContext): void => {
  rememberGenerated(token, context);

  gspx.trackEvent({
    ...defaultTrackEvent,
    name: LOCATION_REQUEST_EVENT_NAMES.GENERATED,
    type: GPX_EVENT_TYPES.API_RESPONSE,
    entities: {
      ...entitiesFor(context),
      token_id: hashTrackingToken(token),
    },
    additionalKeys: additionalKeysFor(context),
  });
};

/**
 * Tracks the generated event when `resolvedPhrases` carries a location request
 * link. Returns whether it did.
 *
 * Any resolve of a message containing the link phrase mints a token, so this is
 * the single place every one of those paths reports it — the Request Location
 * button, the All Messages modal and Suggested Responses alike. Callers driving
 * the button treat `false` as a failure; callers resolving whatever message the
 * dispatcher picked ignore it, because most messages carry no link at all.
 */
export const trackLocationRequestGeneratedFromPhrases = (
  resolvedPhrases: ResolvedLocationPhrases,
  context: LocationRequestContext,
): boolean => {
  const token = extractLocationRequestToken(
    resolvedPhrases.LocationRequestLink,
  );
  if (!token) return false;

  trackLocationRequestGenerated({ ...context, token });
  return true;
};

/**
 * Tracks a click that could not produce a location request link. `token_id` is
 * absent by definition — the failure is that no token exists.
 */
export const trackLocationRequestFailed = ({
  failureReason,
  ...context
}: LocationRequestContext & {
  failureReason: LocationRequestFailureReason;
}): void => {
  gspx.trackEvent({
    ...defaultTrackEvent,
    name: LOCATION_REQUEST_EVENT_NAMES.FAILED,
    type: GPX_EVENT_TYPES.INFO,
    entities: entitiesFor(context),
    additionalKeys: {
      ...additionalKeysFor(context),
      failure_reason: failureReason,
    },
  });
};

/**
 * Tracks that a message carrying a location request link was actually sent.
 * No-ops if this token has already been reported sent.
 *
 * `link_origin` distinguishes the Request Location button funnel from a link
 * sent straight out of the All Messages modal or Suggested Responses.
 */
export const trackLocationRequestSent = ({
  token,
  ...context
}: LocationRequestTokenContext): void => {
  if (sentTokens.has(token)) return;
  rememberSent(token);

  const generatedContext = generatedContexts.get(token);

  gspx.trackEvent({
    ...defaultTrackEvent,
    name: LOCATION_REQUEST_EVENT_NAMES.SENT,
    type: GPX_EVENT_TYPES.CLICK,
    entities: {
      ...entitiesFor(context),
      token_id: hashTrackingToken(token),
    },
    additionalKeys: {
      ...additionalKeysFor({
        ...context,
        // The outbound composer hands a 409 off to the in-call TF911 chat,
        // which is the surface that actually sends. Inheriting is_outbound from
        // where the link was minted keeps one funnel filterable end to end.
        isOutbound: generatedContext?.isOutbound ?? context.isOutbound,
      }),
      link_origin: generatedContext ? 'request_location_button' : 'other',
    },
  });
};

/**
 * Tracks a sent location request observed on a TalkJS session's onMessage.
 *
 * Both guards matter. createTalkSession returns a process-wide singleton, so
 * every onMessage handler sees every message in every conversation the user
 * participates in — without the conversation check, an RCS send would also be
 * reported as TF911. And onMessage fires for inbound messages too, so isByMe
 * keeps a citizen echoing the link back from counting as a send.
 */
export const trackLocationRequestSentFromMessage = (
  message: TrackableChatMessage,
  context: LocationRequestContext & { conversationId: string },
): void => {
  if (!message.isByMe) return;
  if (message.conversation.id !== context.conversationId) return;

  const token = extractLocationRequestToken(message.body);
  if (!token) return;

  trackLocationRequestSent({ ...context, token });
};
